import React from 'react';
import dayjs from 'dayjs';

import { creditcardDepositConfirm, creditcardCreateDepositRequest } from '../../../../api/deposit';
import PortalSessionContext from '../../../../contexts/PortalSessionContext/PortalSessionContext';
import { parseApiError } from '../../../../utils/parseApiError';
import CardIframe from './CardIframe';
import DepositSuccessfulMessage from './DepositSuccessfulMessage';
import DepositUnsuccessfulMessage from './DepositUnsuccessfulMessage';
import formatCurrency from '../../../../utils/formatCurrency';

// edit this to take a credit card object and immediately create the FZ.

const CreditCard: React.FC<Props> = ({
	amount = 0,
	selectedPromotion,
	setAmountError,
	userUpdater,
	savedCreditCard,
	isOnboarding,
	close,
	onTryAgain,
}: Props) => {
	const { user } = React.useContext(PortalSessionContext);

	const [showSuccessfulMessage, setShowSuccessfulMessage] = React.useState<boolean>(false);
	const [showUnsuccessfulMessage, setShowUnsuccessfulMessage] = React.useState<boolean>(false);
	const errorMessageRef = React.useRef(null);

	const [depositState, setDepositState] = React.useState<{
		error?: null | string;
		processing: boolean;
		done: boolean;
	}>({
		error: null,
		processing: false,
		done: false,
	});
	const [fatzebra, setFatzebra] = React.useState<{
		cardToken?: string;
		payment: {
			amount: number;
			currency: string;
			reference: string;
		};
		token: string;
		verification: string;
	} | null>(null);

	// not sure why this was made in such a convoluted way, this crap should be reviewed/refactored next time this component is touched
	const selectedPromotionRef = React.useRef(selectedPromotion);

	React.useEffect(() => {
		if (!((selectedPromotion === undefined || selectedPromotion === null) && selectedPromotionRef.current)) {
			selectedPromotionRef.current = selectedPromotion;
		}
	}, [selectedPromotion]);

	React.useEffect(() => {
		if (!savedCreditCard) {
			handleCreateDepositRequest();
		} else {
			handleCreateDepositRequest(savedCreditCard);
		}
		return () => {
			setFatzebra(null);
		};
	}, []);

	const handleCreateDepositRequest = (card?: PortalUserCard) => {
		setDepositState({
			...depositState,
			error: null,
			done: false,
		});

		setFatzebra(null);

		const promoDetails =
			selectedPromotionRef.current?.id && selectedPromotionRef.current?.client?.id
				? {
						id: selectedPromotionRef.current?.id,
						client_id: selectedPromotionRef.current?.client.id,
				  }
				: undefined;

		creditcardCreateDepositRequest(amount, card?.id ?? undefined, promoDetails)
			.then((response) => {
				setFatzebra({
					cardToken: card?.token,
					payment: response.data.payment,
					token: response.data.token,
					verification: response.data.verification,
				});
			})
			.catch((error) => {
				console.log(error);
				setDepositState({
					...depositState,
					error: parseApiError(error.errors),
				});

				setAmountError(parseApiError(error.errors, 'amount'));
			});
	};

	// in case the iframe is doing a 3DS check it will nee to call this callback with a payload (fzExtra) from fatzebra
	const threeDSCheckCallback = React.useCallback(
		(fzExtra: unknown) => {
			const promoDetails =
				selectedPromotionRef.current?.id && selectedPromotionRef.current?.client?.id
					? {
							id: selectedPromotionRef.current?.id,
							client_id: selectedPromotionRef.current?.client.id,
					  }
					: undefined;

			return creditcardDepositConfirm(amount, savedCreditCard?.id as number, fzExtra, promoDetails);
		},
		[amount, savedCreditCard?.id]
	);

	const handleCloseAdd = React.useCallback(() => {
		setFatzebra(null);
		userUpdater();
		close();
	}, [close, userUpdater]);

	const setErrorMessage = React.useCallback((errorMessage) => {
		errorMessageRef.current = errorMessage;
	}, []);

	const [promotionDetails, promotionType] = React.useMemo(() => {
		let brandName: string, promotionType: 'betting' | 'fantasy';

		switch (selectedPromotionRef.current?.client.id) {
			case 1:
				promotionType = 'betting';
				brandName = 'PlayUp';

				break;
			case 2:
				promotionType = 'fantasy';
				brandName = 'Draftstars';
				break;
			default:
				promotionType = 'betting';
				brandName = '';
		}

		let message = `Max${brandName && ' ' + brandName} bonus: ${formatCurrency(
			selectedPromotionRef.current?.rules?.max_bonus_amount
		)}. `;

		if (brandName) {
			message += `Redeemable only on ${brandName}. `;
		}

		if (!selectedPromotionRef.current?.rules?.never_expire && selectedPromotionRef.current?.expired_at) {
			message += `Valid until ${dayjs(selectedPromotionRef.current?.expired_at).format('MMM DD [at] HH:mm a')}`;
		}
		const promotionDetails = {
			title: selectedPromotionRef.current?.name,
			content: message,
			clientId: selectedPromotionRef.current?.client.id,
		};

		return [promotionDetails, promotionType];
	}, []);

	return (
		<div className="_mb-4 _mt-4">
			{fatzebra && (
				<>
					{!showSuccessfulMessage && !showUnsuccessfulMessage && (
						<CardIframe
							fatzebra={fatzebra}
							threeDSCheckCallback={threeDSCheckCallback}
							user={user as PortalUser}
							showSuccessfulResultMessage={setShowSuccessfulMessage}
							showUnsuccessfulResultMessage={setShowUnsuccessfulMessage}
							setErrorMessage={setErrorMessage}
							promote={promotionDetails}
						/>
					)}
					{(showSuccessfulMessage || showUnsuccessfulMessage) && (
						<>
							{showSuccessfulMessage && (
								<DepositSuccessfulMessage
									isOnboarding={isOnboarding}
									amount={fatzebra.payment.amount}
									close={handleCloseAdd}
									messageType={promotionType}
								/>
							)}
							{showUnsuccessfulMessage && (
								<DepositUnsuccessfulMessage
									isOnboarding={isOnboarding}
									amount={fatzebra.payment.amount}
									errorMessage={errorMessageRef.current || depositState.error}
									tryAgain={onTryAgain}
									close={handleCloseAdd}
								/>
							)}
						</>
					)}
				</>
			)}
			{depositState.error && <p className="_p-2 _text-sm _text-red-500 _text-center">{depositState.error}</p>}
		</div>
	);
};

export default CreditCard;

interface Props {
	amount?: number;
	selectedPromotion?: DepositPromotion;
	isOnboarding?: boolean;
	setAmountError: (amountError: string | undefined) => void;
	userUpdater: () => void;
	savedCreditCard?: PortalUserCard;
	close: () => void;
	onTryAgain: () => void;
}
